import { defineComponent as _defineComponent } from 'vue'
import { useBusStopCluster } from "@/composables/map/useBusStopCluster";
import { useBusStopMakersWindow } from "@/composables/map/useBusStopMakersWindow";
import { useBusStopMarkers } from "@/composables/map/useBusStopMarkers";
import { useCurrentLocationMarker } from "@/composables/map/useCurrentLocationMarker";
import { useEventListener } from "@/composables/map/useEventListener";
import { useNaverMap } from "@/composables/map/useNaverMap";
import { useRadiusCircle } from "@/composables/map/useRadiusCirlce";
import { useSelectedBusStopMarker } from "@/composables/map/useSelectedBusStopMarker";
import { useSelectedPositionMarker } from "@/composables/map/useSelectedPositionMarker";
import { useUpdateAddress } from "@/composables/map/useUpdateAddress";
import { IAddressSearchInfo } from "@/domain/entities/address";
import { IBusStopData } from "@/domain/entities/busStop";
import { BusStopMarker } from "@/types";
import useCalculateDistance from "@/use/useCalculateDistance";
import _ from "lodash";
import { onBeforeUnmount, onMounted, PropType, watch } from "vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'NaverMap',
  props: {
  latitude: {
    type: Number,
  },
  longitude: {
    type: Number,
  },
  showCurrentLocation: {
    type: Boolean,
    default: false,
  },
  busStops: {
    type: Array as PropType<Array<IBusStopData>> | null,
    default: null,
  },
  currentBusStopIndex: {
    type: Number,
    default: 0,
  },
  selectedBusStop: {
    type: Object as PropType<IBusStopData> | null,
    default: null,
  },
  useCluster: {
    type: Boolean,
    default: false,
  },
  infoWindowContent: {
    type: String,
    default: "",
  },
  infoBorderColor: {
    type: String,
    default: "#707070",
  },
  /** 버스 정류장이 아닌 특정 위치 마커 표시 여부 */
  shouldShowPositionMarker: {
    type: Boolean,
    default: false,
  },
  circleRadiusMeter: {
    type: Number,
    default: 0,
  },
  zoomLevel: {
    type: Number,
    default: 14,
  },
  minZoomLevel: {
    type: Number,
    default: 8,
  },
  maxZoomLevel: {
    type: Number,
    default: 16,
  },
  syncMarkerWithMapCenter: {
    type: Boolean,
    default: false,
  },
},
  emits: ["onDragEndMap", "onZoomChanged", "onChangedMapCenter", "onMapIdle", "onSelectedBusStopMarker", "onUpdateAddress"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

/* useNaverMap - 지도 초기화, 스크립트 로드, 마커 생성/업데이트, 정보창 생성 기능 제공 */
const {
  map,
  addScript,
  initMap,
  getCenter,
  setCenter,
  createMarker,
  updateMarkerStyle,
  createInfoWindow,
  removeInfoWindow,
} = useNaverMap(
  props.latitude ?? 37.2529797,
  props.longitude ?? 127.4806853,
  props.infoBorderColor,
  props.zoomLevel,
  props.minZoomLevel,
  props.maxZoomLevel
);

/* useEventListener - 지도 중심 변경 및 마커 클릭 이벤트 등록/해제 기능 제공 */
const {
  addCenterChangedEventListener,
  removeCenterChangedEventListener,
  addMarkerClickEventListener,
  removeMarkerClickEventListener,
  addDragListener,
  removeDragEventListener,
  addDragEndEventListener,
  removeDragEndEventListener,
  addZoomChangedEventListener,
  removeZoomChangedEventListener,
  addIdleEventListener,
  removeIdleEventListener,
} = useEventListener();

/* 기본 버스정류장 마커 이미지 함수 (기본 마커) */
const getDefaultBusStopMarkerImage = () => {
  return {
    url: require("@/assets/icons/marker.svg"),
    size: new window.naver.maps.Size(20, 20),
    origin: new window.naver.maps.Point(0, 0),
    anchor: new window.naver.maps.Point(10, 10),
  };
};

/* useSelectedBusStopMarker - 선택된 버스정류장 마커 생성, 삭제, 업데이트 */
const emitOnSelectedBusStopMarker = (busStop: IBusStopData) => {
  emit("onSelectedBusStopMarker", busStop);
};

const {
  selectedBusStopMarker,
  getSelectedBusStopMarkerImage,
  createSelectedBusStopMarker,
  removeSelectedBusStopMarker,
  updateSelectedBusStopMarker,
} = useSelectedBusStopMarker(
  createMarker,
  createInfoWindow,
  updateMarkerStyle,
  getDefaultBusStopMarkerImage,
  emitOnSelectedBusStopMarker
);

/* useCurrentLocationMarker - 현재 위치 마커 생성, 삭제 */
const {
  currentLocationMarker,
  createCurrentLocationMarker,
  removeCurrentLocationMarker,
} = useCurrentLocationMarker(createMarker);

/* useSelectedPositionMarker - 버스정류장이 아닌 특정 위치 마커 생성, 삭제 */
const {
  selectedPositionMarker,
  createSelectedPositionMarker,
  removeSelectedPositionMarker,
  updateSelectedPositionMarker,
  getSelectedPositionMarkerCoord,
} = useSelectedPositionMarker(createMarker, createInfoWindow);

/* useBusStopMarkers - 버스정류장 목록 마커 생성, 삭제, 업데이트 */
// 클릭 시 선택된 마커 업데이트 처리 (useSelectedBusStopMarker의 업데이트 호출)
const clickEventCallback = (busStopMarker: BusStopMarker) => {
  updateSelectedBusStopMarker(busStopMarker);
};

const {
  busStopMarkers,
  getBusStopMarkerImage,
  createBusStopMarkers,
  removeBusStopMarkers,
  updateBusStopMarker,
} = useBusStopMarkers(
  selectedBusStopMarker,
  createMarker,
  getSelectedBusStopMarkerImage,
  updateMarkerStyle,
  addMarkerClickEventListener,
  removeMarkerClickEventListener,
  clickEventCallback
);

/* 클러스터 마커 생성 */
const { createBusStopCluster } = useBusStopCluster();

const emitOnUpdateAddress = (address: IAddressSearchInfo) => {
  emit("onUpdateAddress", address);
};

/** 좌표로 주소 찾아서 갱신하기 */
const { debounceUpdateAddress } = useUpdateAddress(emitOnUpdateAddress);

/* 가장 가까운 버스정류장 찾기 */
const findNearestBusStop = (): IBusStopData | undefined => {
  if (!props.latitude || !props.longitude) return;

  let nearestBusStopValue: IBusStopData | undefined = undefined;
  let distance = Number.MAX_VALUE;

  props.busStops?.forEach((busStop) => {
    const newDistance = useCalculateDistance(
      busStop.latitude,
      busStop.longitude,
      props.latitude ?? 0,
      props.longitude ?? 0
    );
    if (newDistance < distance) {
      distance = newDistance;
      nearestBusStopValue = busStop;
    }
  });

  return nearestBusStopValue;
};

/* 버스정류장 마커 생성 처리 */
const handleBusStopMarkers = (busStops?: IBusStopData[]) => {
  if (!map.value) return;

  // 기존 마커 제거
  removeBusStopMarkers(busStops);
  // busStops 목록에 따라 마커 생성
  createBusStopMarkers(map.value, props.busStops, props.selectedBusStop?.id);

  // 클러스터 사용 시 클러스터 생성
  if (props.useCluster) {
    createBusStopCluster(map.value, busStopMarkers.value);
  }
};

/* 현재 위치, 선택된 버스정류장, 특정 위치 마커 생성 처리 */
const handleMarkers = () => {
  if (!map.value) return;

  if (props.showCurrentLocation) {
    createCurrentLocationMarker(map.value, props.latitude, props.longitude);
  } else if (props.shouldShowPositionMarker) {
    createSelectedPositionMarker(
      map.value,
      props.latitude,
      props.longitude,
      props.infoWindowContent
    );
  }

  handleBusStopMarkers();
};

/* 버스정류장 목록이 변경될 때 처리 */
watch(
  () => props.busStops,
  (newBusStops, oldBusStops) => {
    if (!map.value) return;

    // busStops가 변경되면 마커 전체 재생성
    if (newBusStops !== oldBusStops || newBusStops.length === 0) {
      const addedBusStops = _.differenceBy(newBusStops, oldBusStops, "id");
      createBusStopMarkers(map.value, addedBusStops, props.selectedBusStop?.id);

      const removedBusStops = _.differenceBy(oldBusStops, newBusStops, "id");
      removeBusStopMarkers(removedBusStops);

      // TODO: 지도 드래그 시 Window 범위를 벗어나는 경우 정류장 API 호출을 하는데 아래 코드로 인해 지도 센터가 정류장 쪽으로 순간이동됨
      // if (props.showCurrentLocation) {
      //   const nearest = findNearestBusStop();
      //   nearest
      //     ? setCenter(nearest.latitude, nearest.longitude)
      //     : setCenter(props.latitude, props.longitude);
      // }
    }
  }
);

const updateBusStopMarkerWithCenter = (shouldUpdateCenter: boolean) => {
  if (!map.value || !props.selectedBusStop) return;

  // 선택된 버스정류장만 변경되면 마커 스타일 업데이트 및 중심 이동
  updateBusStopMarker(props.selectedBusStop?.id);
  if (shouldUpdateCenter) {
    setCenter(props.selectedBusStop.latitude, props.selectedBusStop.longitude);
  }
};

// 위도/경도 변경 감지 – 지도 중심 이동 및 선택 마커 업데이트
watch(
  () => [props.latitude, props.longitude],
  ([lat, lng], [oldLat, oldLng]) => {
    if (!map.value) return;
    if (lat === oldLat && lng === oldLng) return;

    setCenter(lat, lng);

    if (props.shouldShowPositionMarker) {
      removeSelectedPositionMarker();
      createSelectedPositionMarker(
        map.value,
        lat,
        lng,
        props.infoWindowContent
      );
    } else {
      removeSelectedBusStopMarker();
      createSelectedBusStopMarker(map.value, lat, lng, props.infoWindowContent);
    }
  }
);

const { checkAndUpdateWindow } = useBusStopMakersWindow(map, 10);
const handleBusStopsMarkersWindow = () => {
  const shouldUpdateMarkers = checkAndUpdateWindow();
  if (!shouldUpdateMarkers || !map.value) return;

  // 더 이상 shouldUpdateBusStops 이벤트를 emit하지 않음
  // 부모 컴포넌트에서 idle 이벤트를 통해 처리
};

// 지도에 반경 그리기
const { radiusCircle, createRadiusCircleOnCenter, updateRadiusCircleOnCenter } =
  useRadiusCircle();

const handleRadiusCircle = () => {
  radiusCircle.value
    ? updateRadiusCircleOnCenter(map.value, props.circleRadiusMeter)
    : createRadiusCircleOnCenter(map.value, props.circleRadiusMeter);
};

// 지도에 그리는 원의 반경 업데이트
watch(
  () => props.circleRadiusMeter,
  () => {
    if (!map.value) return;
    handleRadiusCircle();
  }
);

const emitOnChangedMapCenter = (map: any) => {
  if (!props.latitude || !props.longitude) return;

  const center = map.getCenter();
  const isSameAsInitial =
    center.x.toFixed(4) === props.longitude.toFixed(4) &&
    center.y.toFixed(4) === props.latitude.toFixed(4);

  emit("onChangedMapCenter", isSameAsInitial);
};

const emitOnDragEndMap = () => {
  emit("onDragEndMap");
};

const emitOnZoomChanged = (zoomLevel: number) => {
  emit("onZoomChanged", zoomLevel);
};

const emitOnMapIdle = () => {
  emit("onMapIdle");
};

const changePositionMarkerIfNeeded = () => {
  if (!props.syncMarkerWithMapCenter) return;

  removeInfoWindow();
  const { _lat: lat, _lng: lng } = map.value.getCenter();
  updateSelectedPositionMarker(lat, lng);
};

const updateAddressIfNeeded = () => {
  if (!props.syncMarkerWithMapCenter) return;

  const markerCoord = getSelectedPositionMarkerCoord();
  debounceUpdateAddress(markerCoord._lat, markerCoord._lng);

  // 줌 애니메이션의 기준점을 마커의 위치로 변경한다.
  map.value.setOptions("zoomOrigin", markerCoord);
};

// 지도 객체가 생성되면 중심 변경 이벤트 등록
watch(map, (newMap) => {
  if (!newMap) return;

  addCenterChangedEventListener(newMap, () => {
    emitOnChangedMapCenter(newMap);
    handleRadiusCircle();
  });

  addIdleEventListener(newMap, () => {
    emitOnMapIdle();
  });

  addDragListener(newMap, () => {
    changePositionMarkerIfNeeded();
  });

  addDragEndEventListener(newMap, () => {
    emitOnDragEndMap();
    handleBusStopsMarkersWindow();
    updateAddressIfNeeded();
  });

  addZoomChangedEventListener(newMap, () => {
    emitOnZoomChanged(newMap.getZoom());
  });

  handleMarkers();
  handleRadiusCircle();
});

watch(
  () => props.zoomLevel,
  (newZoomLevel) => {
    map.value.setZoom(newZoomLevel, true);
  }
);

/* 지도 초기화 및 이벤트 리스너 등록 */
onMounted(() => {
  window.naver ? initMap() : addScript();
});

const removeAllEventListeners = () => {
  removeCenterChangedEventListener();
  removeMarkerClickEventListener();
  removeDragEventListener();
  removeDragEndEventListener();
  removeZoomChangedEventListener();
  removeIdleEventListener();
};

onBeforeUnmount(() => {
  removeAllEventListeners();
});

__expose({ getCenter, setCenter, updateBusStopMarkerWithCenter });

const __returned__ = { props, emit, map, addScript, initMap, getCenter, setCenter, createMarker, updateMarkerStyle, createInfoWindow, removeInfoWindow, addCenterChangedEventListener, removeCenterChangedEventListener, addMarkerClickEventListener, removeMarkerClickEventListener, addDragListener, removeDragEventListener, addDragEndEventListener, removeDragEndEventListener, addZoomChangedEventListener, removeZoomChangedEventListener, addIdleEventListener, removeIdleEventListener, getDefaultBusStopMarkerImage, emitOnSelectedBusStopMarker, selectedBusStopMarker, getSelectedBusStopMarkerImage, createSelectedBusStopMarker, removeSelectedBusStopMarker, updateSelectedBusStopMarker, currentLocationMarker, createCurrentLocationMarker, removeCurrentLocationMarker, selectedPositionMarker, createSelectedPositionMarker, removeSelectedPositionMarker, updateSelectedPositionMarker, getSelectedPositionMarkerCoord, clickEventCallback, busStopMarkers, getBusStopMarkerImage, createBusStopMarkers, removeBusStopMarkers, updateBusStopMarker, createBusStopCluster, emitOnUpdateAddress, debounceUpdateAddress, findNearestBusStop, handleBusStopMarkers, handleMarkers, updateBusStopMarkerWithCenter, checkAndUpdateWindow, handleBusStopsMarkersWindow, radiusCircle, createRadiusCircleOnCenter, updateRadiusCircleOnCenter, handleRadiusCircle, emitOnChangedMapCenter, emitOnDragEndMap, emitOnZoomChanged, emitOnMapIdle, changePositionMarkerIfNeeded, updateAddressIfNeeded, removeAllEventListeners }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})